// frontend/src/pages/driver/DriverInventory.jsx
import { useState, useEffect } from 'react';
import axios from 'axios';

const API = import.meta.env.VITE_API_URL || 'http://localhost:5000/api/v1';

const DriverInventory = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchInventory();
  }, [filter]);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/driver/inventory`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: filter }
      });
      
      if (response.data.success) {
        setData(response.data.data);
        setStats(response.data.stats);
      }
    } catch (err) {
      console.error('Inventory error:', err);
      setError(err.response?.data?.message || 'Мэдээлэл татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      in_transit: { label: 'Хүргэж байна', bg: 'bg-blue-100', text: 'text-blue-700', icon: '🚚' },
      pending_return: { label: 'Буцаах хүлээгдэж байна', bg: 'bg-orange-100', text: 'text-orange-700', icon: '⚠️' },
      delivered: { label: 'Хүргэсэн', bg: 'bg-green-100', text: 'text-green-700', icon: '✅' },
      returned: { label: 'Буцаасан', bg: 'bg-gray-100', text: 'text-gray-700', icon: '↩️' }
    };
    const cfg = config[status] || config.in_transit;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text} flex items-center gap-1`}>
        <span>{cfg.icon}</span>
        {cfg.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Уншиж байна...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-800">📦 Миний бараа</h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 text-center">
          <p className="text-2xl font-bold text-blue-600">{stats.in_transit || 0}</p>
          <p className="text-xs text-gray-500">Хүргэж байгаа</p>
        </div>
        <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 text-center">
          <p className="text-2xl font-bold text-orange-600">{stats.pending_return || 0}</p>
          <p className="text-xs text-gray-500">Буцаах</p>
        </div>
        <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 text-center">
          <p className="text-2xl font-bold text-gray-600">{stats.total_items || 0}</p>
          <p className="text-xs text-gray-500">Нийт бараа</p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { value: 'all', label: 'Бүгд', icon: '📋' },
          { value: 'in_transit', label: 'Хүргэж байгаа', icon: '🚚' },
          { value: 'pending_return', label: 'Буцаах', icon: '⚠️' }
        ].map((tab) => (
          <button
            key={tab.value}
            onClick={() => setFilter(tab.value)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium whitespace-nowrap transition ${
              filter === tab.value
                ? 'bg-blue-500 text-white shadow-lg'
                : 'bg-white text-gray-600 border border-gray-200'
            }`}
          >
            <span>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          <p>{error}</p>
        </div>
      )}

      {/* Inventory List */}
      {data.length === 0 ? (
        <div className="bg-white rounded-2xl p-8 text-center">
          <span className="text-6xl mb-4 block">📭</span>
          <p className="text-gray-500">Бараа олдсонгүй</p>
        </div>
      ) : (
        <div className="space-y-4">
          {data.map((group) => (
            <div key={group.delivery_number} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Delivery Header */}
              <div className={`p-4 border-b ${
                group.inventory_status === 'pending_return' 
                  ? 'bg-orange-50 border-orange-200' 
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="font-bold text-gray-800">📦 {group.delivery_number}</p>
                  {getStatusBadge(group.inventory_status)}
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">👤 {group.customer_name}</span>
                  <span className="text-gray-500">{group.shop_name}</span>
                </div>
              </div>

              {/* Items */}
              <div className="divide-y divide-gray-100">
                {group.items.map((item) => (
                  <div key={item.id} className="p-3 flex items-center gap-3">
                    {item.product_image ? (
                      <img 
                        src={item.product_image} 
                        alt="" 
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                        📦
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-gray-800 text-sm">{item.product_name}</p>
                      {item.variant_name && (
                        <p className="text-xs text-gray-500">{item.variant_name}</p>
                      )}
                      <p className="text-xs text-gray-400">{item.product_sku}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg text-gray-800">x{item.quantity}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Info for pending_return */}
              {group.inventory_status === 'pending_return' && (
                <div className="p-3 bg-orange-50 border-t border-orange-200">
                  <p className="text-sm text-orange-700 flex items-center gap-2">
                    <span>💡</span>
                    Агуулахад буцааж өгнө үү
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DriverInventory;